import React from 'react';

import i18n from 'util/i18n';

import { Summary, DateSummary } from 'components/Summary';

import { SUBSTANCE_USE, SUBSTANCE_USE_DRUGS_MISUSE } from 'config/formSections/substanceUse';
import { formConfig } from 'config/forms';
import { getNumberOfYearsString } from 'helpers/text';

import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import PrescriptionUse from './PrescriptionUse';

const sectionConfig = {
  key: SUBSTANCE_USE_DRUGS_MISUSE.key,
  section: SUBSTANCE_USE.name,
  store: SUBSTANCE_USE.store,
  subsection: SUBSTANCE_USE_DRUGS_MISUSE.name,
  storeKey: SUBSTANCE_USE_DRUGS_MISUSE.storeKey,
};

export class PrescriptionUses extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.updateList = this.updateList.bind(this);
    this.updateMisusedDrugs = this.updateMisusedDrugs.bind(this);
    this.summary = this.summary.bind(this);
  }

  update = (updateValues) => {
    this.props.onUpdate(this.storeKey, {
      MisusedDrugs: this.props.MisusedDrugs,
      List: this.props.List,
      Comments: this.props.Comments,
      SummaryComments: this.props.SummaryComments,
      ...updateValues,
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateMisusedDrugs = (values) => {
    this.update({
      MisusedDrugs: values,
      List: values.value === 'Yes' ? this.props.List : [],
      SummaryComments: values.value === 'Yes' ? this.props.SummaryComments : {},
    });
  };

  updateComments = (values) => {
    this.update({
      Comments: values,
    });
  };

  updateSummaryComments = (values) => {
    this.update({
      SummaryComments: values,
    });
  };

  summary = (item, index) => {
    const o = (item || {}).Item || {};
    const range = DateSummary(o.InvolvementDates);
    const name = (o.PrescriptionName || {}).value;

    return Summary({
      type: i18n.t('substance.drugs.prescription.collection.itemType'),
      index,
      left: name,
      right: range,
      placeholder: i18n.t('substance.drugs.prescription.collection.summary'),
    });
  };

  render() {
    const {
      formType, requireDrugWhileSafety, requireDrugWithClearance, errors
    } = this.props;

    const formTypeConfig = formType && formConfig[formType];
    const years = formTypeConfig && formTypeConfig.SUBSTANCE_DRUG_USE_YEARS;
    const numberOfYearsString = getNumberOfYearsString(years);

    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div
        className="section-content prescription-uses"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_DRUGS_MISUSE.key}
      >
        <h1 className="section-header">{i18n.t('substance.pvq.subsection.drugs.misuse')}</h1>
        <Branch
          attribute="MisusedDrugs"
          name="Misused"
          label={i18n.t('substance.pvq.drugs.heading.prescriptionUses', { numberOfYearsString })}
          labelSize="h4"
          className="misused"
          {...this.props.MisusedDrugs}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateMisusedDrugs}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Show when={this.props.MisusedDrugs.value === 'Yes'}>
          <Accordion
            attribute="List"
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.pvq.drugs.prescription.collection.description')}
            appendTitle={i18n.t('substance.pvq.drugs.prescription.collection.appendTitle', {
              numberOfYearsString,
            })}
            appendLabel={i18n.t('substance.pvq.drugs.prescription.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          >
            <PrescriptionUse
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              requireDrugWhileSafety={requireDrugWhileSafety}
              requireDrugWithClearance={requireDrugWithClearance}
              formType={this.props.formType}
            />
          </Accordion>
        </Show>
      </div>
    ) : (
      <div
        className="section-content prescription-uses"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_DRUGS_MISUSE.key}
      >
        <h1 className="section-header">{i18n.t('substance.subsection.drugs.misuse')}</h1>
        <Branch
          attribute="MisusedDrugs"
          name="Misused"
          label={i18n.t('substance.drugs.heading.prescriptionUses', { numberOfYearsString })}
          labelSize="h4"
          className="misused"
          {...this.props.MisusedDrugs}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateMisusedDrugs}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Comments
          title={i18n.t('substance.comments.optional')}
          name="Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
        <Show when={this.props.MisusedDrugs.value === 'Yes'}>
          <Accordion
            attribute="List"
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.drugs.prescription.collection.description')}
            appendTitle={i18n.t('substance.drugs.prescription.collection.appendTitle', {
              numberOfYearsString,
            })}
            appendLabel={i18n.t('substance.drugs.prescription.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          >
            <PrescriptionUse
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              requireDrugWhileSafety={requireDrugWhileSafety}
              requireDrugWithClearance={requireDrugWithClearance}
            />
          </Accordion>
          <Comments
            title={i18n.t('substance.comments.summary')}
            name="SummaryComments"
            className="summary-comment"
            attribute="SummaryComments"
            {...this.props.SummaryComments}
            onUpdate={this.updateSummaryComments}
            required={false}
          />
        </Show>
      </div>
    );
  }
}

PrescriptionUses.defaultProps = {
  MisusedDrugs: {},
  List: { items: [], branch: {} },
  onError: (value, arr) => arr,
  section: 'substance',
  subsection: 'drugs/misuse',
  dispatch: () => { },
  scrollToBottom: '',
  errors: [],
};

export default connectSubsection(PrescriptionUses, sectionConfig);
